import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "../lib/api";
import { formatDateTime } from "../lib/utils";
import { Eye, Send, Loader2, RefreshCw } from "lucide-react";

interface DailyReportConfig {
  id: string;
  botId: string;
  botName: string;
  mode: "live" | "onboarding" | "auto";
  clientChatId: string | null;
  dryRun: boolean;
  isActive: boolean;
  lastSentAt: string | null;
  lastReport:
    | {
        text?: string;
        targetChatId?: string;
        dryRun?: boolean;
        generatedAt?: string;
        metrics?: { userMsgs?: number; botMsgs?: number; chats?: number };
      }
    | null;
  createdAt: string;
  updatedAt: string;
}

interface RunResult {
  botId: string;
  botName: string;
  mode: string;
  skipped?: string;
  dryRun: boolean;
  targetChatId: string | null;
  text?: string;
  send?: { ok: boolean; error?: string };
}

export default function DailyReportsPage() {
  const [previewBotId, setPreviewBotId] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const { data: configs, isLoading } = useQuery<DailyReportConfig[]>({
    queryKey: ["/api/admin/daily-reports/configs"],
  });

  const toggleDryRun = useMutation({
    mutationFn: async ({ botId, dryRun }: { botId: string; dryRun: boolean }) => {
      const res = await apiRequest(
        "PATCH",
        `/api/admin/daily-reports/configs/${botId}/dry-run`,
        { dryRun },
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/daily-reports/configs"] });
    },
  });

  const runNow = useMutation({
    mutationFn: async ({ botId, force }: { botId: string; force: boolean }) => {
      const res = await apiRequest("POST", "/api/admin/daily-reports/run", {
        onlyBotId: botId,
        forceDryRun: force,
      });
      return (await res.json()) as { results: RunResult[] };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/daily-reports/configs"] });
    },
  });

  const handlePreview = async (botId: string) => {
    setPreviewBotId(botId);
    setPreviewText(null);
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const res = await apiRequest("POST", "/api/admin/daily-reports/run", {
        onlyBotId: botId,
        previewOnly: true,
      });
      const data = (await res.json()) as { results: RunResult[] };
      const r = data.results?.[0];
      if (!r) {
        setPreviewError("Нет конфига для этого бота");
      } else if (r.skipped) {
        setPreviewError(`Пропущено: ${r.skipped}`);
      } else if (!r.text) {
        setPreviewError("Превью пустое (бот без сообщений за вчера?)");
      } else {
        setPreviewText(r.text);
      }
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : String(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRunNow = async (botId: string, dryRun: boolean) => {
    const label = dryRun
      ? "Отправить отчёт в DM Анатолия (dry-run)?"
      : "Отправить отчёт КЛИЕНТУ прямо сейчас?";
    if (!confirm(label)) return;
    await runNow.mutateAsync({ botId, force: dryRun });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Daily Reports</h1>
        <span className="text-sm text-muted-foreground">
          {configs ? `${configs.length} ботов в расписании` : ""}
        </span>
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Бот
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Режим
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Чат клиента
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Dry-run
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Последний отчёт
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : configs?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                    Нет конфигов
                  </td>
                </tr>
              ) : (
                configs?.map((cfg) => (
                  <tr key={cfg.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <div className="font-medium text-sm">{cfg.botName}</div>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5">
                        {cfg.botId.slice(0, 8)}…
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                        {cfg.mode}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono">
                      {cfg.clientChatId ?? <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() =>
                          toggleDryRun.mutate({ botId: cfg.botId, dryRun: !cfg.dryRun })
                        }
                        disabled={toggleDryRun.isPending}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition ${
                          cfg.dryRun
                            ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                            : "bg-green-100 text-green-800 hover:bg-green-200"
                        } disabled:opacity-50`}
                        title={
                          cfg.dryRun
                            ? "Сейчас dry-run: отправляется в DM Анатолия. Нажми чтобы переключить в LIVE → отправлять клиенту."
                            : "Сейчас LIVE: отправляется клиенту. Нажми чтобы вернуть в dry-run."
                        }
                      >
                        {cfg.dryRun ? "🧪 dry-run" : "🟢 LIVE"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {cfg.lastSentAt ? (
                        <div>
                          <div>{formatDateTime(cfg.lastSentAt)}</div>
                          {cfg.lastReport?.metrics && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {cfg.lastReport.metrics.chats} диалогов,{" "}
                              {(cfg.lastReport.metrics.userMsgs ?? 0) +
                                (cfg.lastReport.metrics.botMsgs ?? 0)}{" "}
                              сообщений
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">никогда</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePreview(cfg.botId)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-border hover:bg-muted"
                          title="Сгенерировать отчёт за вчера, ничего не отправляя"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Превью
                        </button>
                        <button
                          onClick={() => handleRunNow(cfg.botId, cfg.dryRun)}
                          disabled={runNow.isPending}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-border hover:bg-muted disabled:opacity-50"
                          title={
                            cfg.dryRun
                              ? "Прогнать сейчас и отправить в DM Анатолия"
                              : "ВНИМАНИЕ: отправит клиенту в реальный чат"
                          }
                        >
                          {runNow.isPending ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Send className="w-3.5 h-3.5" />
                          )}
                          Отправить
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Preview modal */}
      {previewBotId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setPreviewBotId(null)}
        >
          <div
            className="bg-background rounded-lg border border-border shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <h2 className="font-semibold">
                Превью отчёта —{" "}
                {configs?.find((c) => c.botId === previewBotId)?.botName}
              </h2>
              <button
                onClick={() => setPreviewBotId(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              {previewLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Генерируем отчёт (LLM до 45 сек)…
                </div>
              ) : previewError ? (
                <div className="text-sm text-red-600">{previewError}</div>
              ) : previewText ? (
                <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed">
                  {previewText}
                </pre>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
